import { type ReactNode, useEffect, useRef } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { ShopClaimActions, ShopClaimState } from "./shop-claim-model";

export function ShopClaimPage() {
  const state = useSubtree(ShopClaimState);
  const actions = useSubtree(ShopClaimActions);
  const observer = useObserver();
  const token = observer.watch(state.token);
  const loading = observer.watch(state.loading);
  const profileFailed = observer.watch(state.profileFailed);
  const registered = observer.watch(state.registered);
  const flow = observer.watch(state.flow);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-app px-6 text-center">
        <p role="status" aria-live="polite" className="text-sm text-muted">
          Loading your account…
        </p>
      </main>
    );
  }

  if (profileFailed) {
    return (
      <ClaimDialog title="Your account is unavailable" onClose={actions.goToLogin}>
        <p className="text-sm leading-6 text-muted">
          We couldn’t load your account. Sign in again, then reopen this invitation.
        </p>
        <button type="button" className="btn-primary mt-6" onClick={actions.goToLogin}>
          Go to sign in
        </button>
      </ClaimDialog>
    );
  }

  if (!token) {
    return (
      <ClaimDialog title="Invitation unavailable" onClose={actions.close}>
        <p role="alert" className="text-sm leading-6 text-muted">
          This claim link is incomplete. Reopen the invitation email to try again.
        </p>
        <button type="button" className="btn-secondary mt-6" onClick={actions.close}>
          Close
        </button>
      </ClaimDialog>
    );
  }

  if (!registered) {
    const signingIn = flow.step === "login" && flow.busy;
    return (
      <ClaimPanel title="Claim your shop">
        <p className="mx-auto max-w-136 text-center text-base leading-relaxed text-muted sm:text-[21px] sm:leading-[1.55]">
          If you’re the owner of the shop in this invitation, continue with Google or Apple to review its details, claim
          your shop, and start selling your cards with HobbyScan Pro.
        </p>
        <ClaimFeeNotice />
        {flow.step === "login" && flow.error && (
          <p role="alert" className="note note-danger mt-6 text-center text-danger">
            {flow.error}
          </p>
        )}
        <div className="mt-7 flex flex-col gap-4">
          <button
            type="button"
            className="btn-secondary min-h-[70px] w-full text-base sm:text-xl"
            disabled={signingIn}
            onClick={() => void actions.continueWithGoogle()}
          >
            {signingIn ? "Signing in…" : "Continue with Google"}
          </button>
          <button
            type="button"
            className="btn-secondary min-h-[70px] w-full text-base sm:text-xl"
            disabled={signingIn}
            onClick={() => void actions.continueWithApple()}
          >
            {signingIn ? "Signing in…" : "Continue with Apple"}
          </button>
          <button
            type="button"
            className="mt-1 text-sm text-muted underline-offset-4 hover:text-white"
            onClick={actions.close}
          >
            Cancel claim
          </button>
        </div>
      </ClaimPanel>
    );
  }

  if (flow.step === "checking" || flow.step === "login") {
    return (
      <ClaimPanel title="Checking your invitation…">
        <p role="status" className="text-center text-sm text-muted">
          One moment.
        </p>
      </ClaimPanel>
    );
  }

  if (flow.step === "preview-error") {
    return (
      <ClaimPanel title="Invitation unavailable">
        <p role="alert" className="text-sm leading-6 text-muted">
          {flow.error}
        </p>
        <button type="button" className="btn-secondary mt-6 w-full" onClick={() => void actions.checkInvitation()}>
          Try again
        </button>
        <button type="button" className="mt-4 w-full text-sm text-muted hover:text-white" onClick={actions.close}>
          Close
        </button>
      </ClaimPanel>
    );
  }

  const name = flow.shop.name?.trim() || "Your shop";
  const logo = safeHttpUrl(flow.shop.logoUrl);
  const website = safeHttpUrl(flow.shop.websiteUrl);

  return (
    <ClaimPanel title="Claim your shop">
      <p className="mx-auto max-w-136 text-center text-base leading-relaxed text-muted sm:text-[21px] sm:leading-[1.55]">
        If you’re the owner of <strong className="font-semibold text-white">“{name}”</strong>, connect it to your
        HobbyScan Pro account to start selling your cards.
      </p>
      {logo && (
        <img
          src={logo}
          alt=""
          referrerPolicy="no-referrer"
          className="mx-auto mt-5 h-14 w-14 rounded-full border border-line object-cover"
        />
      )}
      {flow.shop.description && (
        <p className="mt-4 whitespace-pre-line wrap-break-word text-center text-sm leading-6 text-muted">
          {flow.shop.description}
        </p>
      )}
      {website && (
        <a
          href={website}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-block w-full text-center text-sm text-accent underline underline-offset-4"
        >
          Visit shop website
        </a>
      )}
      <ClaimFeeNotice />
      {flow.error && (
        <p role="alert" className="note note-danger mt-6 text-center text-danger">
          {flow.error}
        </p>
      )}
      <div className="mt-7 flex flex-col gap-4">
        <button
          type="button"
          className="btn-secondary min-h-[70px] w-full text-base sm:text-xl"
          disabled={flow.busy}
          onClick={() => void actions.connect()}
        >
          {flow.busy ? "Connecting…" : flow.error ? "Try again" : "Connect shop"}
        </button>
        <button
          type="button"
          className="text-sm text-muted underline-offset-4 hover:text-white"
          disabled={flow.busy}
          onClick={actions.close}
        >
          Cancel claim
        </button>
      </div>
    </ClaimPanel>
  );
}

function ClaimDialog({
  title,
  onClose,
  children,
  dismissible = true,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  dismissible?: boolean;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    const cancel = (event: Event) => {
      if (!dismissible) event.preventDefault();
      else onClose();
    };
    dialog.addEventListener("cancel", cancel);
    return () => {
      dialog.removeEventListener("cancel", cancel);
      if (dialog.open) dialog.close();
    };
  }, [dismissible, onClose]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-app px-4 py-8">
      <dialog
        ref={dialogRef}
        aria-labelledby="claim-dialog-title"
        className="m-auto max-h-[min(90vh,40rem)] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-card border border-line bg-app p-0 text-white shadow-2xl backdrop:bg-black/70"
      >
        <section className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <h1 id="claim-dialog-title" className="min-w-0 wrap-break-word text-lg font-semibold tracking-tight">
              {title}
            </h1>
            <button type="button" className="btn-chip shrink-0" disabled={!dismissible} onClick={onClose}>
              Close
            </button>
          </div>
          <div className="mt-4">{children}</div>
        </section>
      </dialog>
    </main>
  );
}

function ClaimPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-app px-4 py-4 sm:px-6 sm:py-8">
      <section
        aria-labelledby="claim-panel-title"
        className="panel w-full max-w-[620px] rounded-3xl px-6 py-8 sm:px-10 sm:py-10 lg:px-11 lg:py-12"
      >
        <StorefrontIcon />
        <h1
          id="claim-panel-title"
          className="mt-7 wrap-break-word text-center text-3xl font-semibold tracking-tight text-white sm:text-[42px]"
        >
          {title}
        </h1>
        <div className="mt-6">{children}</div>
      </section>
    </main>
  );
}

function StorefrontIcon() {
  return (
    <div
      aria-hidden="true"
      className="mx-auto flex h-32 w-32 items-center justify-center rounded-full bg-accent/5 text-accent sm:h-40 sm:w-40"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 64 64"
        fill="none"
        className="h-[76px] w-[76px]"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m12 27 4-14h32l4 14" />
        <path d="M14 27v27h36V27" />
        <path d="M12 27c0 5 7 6 10 1 3 5 9 5 12 0 3 5 9 5 12 0 3 5 8 4 8-1" />
        <path d="M25 54V36h14v18" />
      </svg>
    </div>
  );
}

function ClaimFeeNotice() {
  return (
    <div className="note note-accent mt-7 flex min-h-16 items-center justify-center gap-4 px-4 text-center text-accent sm:mt-8 sm:gap-6 sm:text-lg">
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        className="h-7 w-7 shrink-0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M13 3H5L2 11l8 8 8-8V6l-5-3Z" />
        <circle cx="13" cy="8" r="1" fill="currentColor" stroke="none" />
      </svg>
      <span>
        No monthly fee. Just <strong>1%</strong> of sales.
      </span>
    </div>
  );
}

function safeHttpUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : null;
  } catch {
    return null;
  }
}
